import { FC, ReactNode, useEffect, useMemo, useRef } from "react";
import confetti from "canvas-confetti";
import type { Options } from "canvas-confetti";
import { ArrowRight, Dumbbell, Home, TrendingUp } from "lucide-react";
import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { getRandomBrandImage } from "@assets/brand_images";
import summaryIcon from "@assets/icons/workout-completion-summary.svg";
import flameIcon from "@assets/icons/workout-completion-flame.svg";
import timeIcon from "@assets/icons/workout-completion-time.svg";
import "./workout-completion-screen.css";

const CONFETTI_COLORS = [
  "#dff72b",
  "#2367ff",
  "#ff5b4a",
  "#ffad18",
  "#9c3ff8",
  "#ffffff",
];

const CONFETTI_OPTIONS: Options = {
  colors: CONFETTI_COLORS,
  decay: 0.94,
  disableForReducedMotion: true,
  drift: 0,
  gravity: 0.65,
  scalar: 0.82,
  shapes: ["square", "circle"],
  ticks: 120,
  zIndex: 2147483647,
};

type SummaryConfettiCannon = ReturnType<typeof confetti.create>;

const metricBadgeAccentClasses: Record<
  "blue" | "lime" | "red" | "violet",
  string
> = {
  blue: "workout-completion-metric-badge--blue",
  lime: "workout-completion-metric-badge--lime",
  red: "workout-completion-metric-badge--red",
  violet: "workout-completion-metric-badge--violet",
};

const CompletionMetricCard: FC<{
  accent: "blue" | "lime" | "red" | "violet";
  description: string;
  icon: ReactNode;
  label: string;
  progressValue?: number;
  suffix?: string;
  value: string;
}> = ({ accent, description, icon, label, progressValue, suffix, value }) => (
  <article
    className={`workout-completion-metric-card${
      typeof progressValue === "number"
        ? " workout-completion-metric-card--progress"
        : ""
    }`}
  >
    <div className="flex min-h-9 w-full items-center">
      <div
        className={`workout-completion-metric-badge ${metricBadgeAccentClasses[accent]}`}
      >
        <span className="workout-completion-metric-icon">{icon}</span>
        <span className="workout-completion-metric-label">{label}</span>
      </div>
    </div>
    <div className="workout-completion-metric-value tabular-nums">
      <strong className="font-sans text-[41px] font-medium leading-none text-[#030305]">
        {value}
      </strong>
      {suffix && (
        <span className="font-sans text-[15px] font-bold uppercase leading-none text-[#6f7282]">
          {suffix}
        </span>
      )}
    </div>
    {typeof progressValue === "number" && (
      <div
        className="workout-completion-progress-bar"
        aria-hidden="true"
      >
        <span
          style={{
            width: `${Math.max(0, Math.min(progressValue, 100))}%`,
          }}
        />
      </div>
    )}
    <p className="workout-completion-metric-description">
      {description}
    </p>
  </article>
);

const CompletionSummaryPill: FC<{
  children: ReactNode;
  icon: ReactNode;
}> = ({ children, icon }) => (
  <span className="workout-completion-summary-pill">
    {icon}
    {children}
  </span>
);

const CompletionSvgIcon: FC<{
  className?: string;
  src: string;
}> = ({ className, src }) => (
  <img className={className} src={src} alt="" aria-hidden="true" />
);

const getSummaryConfettiOrigin = (summaryElement: HTMLElement) => {
  const rect = summaryElement.getBoundingClientRect();

  return {
    x: (rect.left + rect.width / 2) / window.innerWidth,
    y: Math.max(0.04, rect.top / window.innerHeight),
  };
};

const runSummaryConfetti = (
  summaryElement: HTMLElement,
  cannon: SummaryConfettiCannon,
) => {
  const summaryOrigin = getSummaryConfettiOrigin(summaryElement);
  const timers: number[] = [];
  const fire = (options: Options) => {
    void cannon({
      ...CONFETTI_OPTIONS,
      ...options,
      origin: options.origin ?? summaryOrigin,
    });
  };
  const schedule = (callback: () => void, delay: number) => {
    timers.push(window.setTimeout(callback, delay));
  };

  fire({
    angle: 90,
    origin: summaryOrigin,
    particleCount: 76,
    spread: 74,
    startVelocity: 34,
  });

  schedule(() => {
    fire({
      angle: 62,
      drift: 0.12,
      origin: { x: 0.08, y: summaryOrigin.y },
      particleCount: 46,
      spread: 58,
      startVelocity: 31,
    });

    fire({
      angle: 118,
      drift: -0.12,
      origin: { x: 0.92, y: summaryOrigin.y },
      particleCount: 46,
      spread: 58,
      startVelocity: 31,
    });
  }, 110);

  schedule(() => {
    fire({
      angle: 90,
      origin: { x: 0.25, y: summaryOrigin.y },
      particleCount: 34,
      scalar: 0.68,
      spread: 72,
      startVelocity: 22,
      ticks: 58,
    });

    fire({
      angle: 90,
      origin: { x: 0.75, y: summaryOrigin.y },
      particleCount: 34,
      scalar: 0.68,
      spread: 72,
      startVelocity: 22,
      ticks: 58,
    });
  }, 260);

  return () => {
    timers.forEach(window.clearTimeout);
    cannon.reset();
  };
};

export const WorkoutCompletionScreen: FC<{
  calories: number;
  completedExercises: number;
  exercisesAmount: number;
  onDiscussWorkout?: () => void;
  onHome: () => void;
  totalSeconds: number;
  totalMinutes: number;
}> = ({
  calories,
  completedExercises,
  exercisesAmount,
  onDiscussWorkout,
  onHome,
  totalSeconds,
  totalMinutes,
}) => {
  const brandImage = useMemo(() => getRandomBrandImage(), []);
  const confettiCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const summaryRef = useRef<HTMLElement | null>(null);
  const progress =
    exercisesAmount > 0
      ? Math.round((completedExercises / exercisesAmount) * 100)
      : 0;
  const seconds = totalSeconds % 60;

  useEffect(() => {
    const summaryElement = summaryRef.current;
    const canvasElement = confettiCanvasRef.current;

    if (!summaryElement || !canvasElement) {
      return;
    }

    const cannon = confetti.create(canvasElement, {
      disableForReducedMotion: true,
      resize: true,
    });
    let cleanupConfetti: (() => void) | undefined;
    let frameId = window.requestAnimationFrame(() => {
      frameId = window.requestAnimationFrame(() => {
        cleanupConfetti = runSummaryConfetti(summaryElement, cannon);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (cleanupConfetti) {
        cleanupConfetti();
        return;
      }

      cannon.reset();
    };
  }, []);

  return (
    <DefaultPage className="workout-completion-page" noPadding>
      <canvas
        ref={confettiCanvasRef}
        className="workout-completion-confetti-canvas"
        aria-hidden="true"
      />
      <Content className="workout-completion-content" scrollY={true}>
        <main className="workout-completion-shell">
          <section
            className="workout-completion-hero"
            aria-label="Тренировка завершена"
          >
            {brandImage && (
              <img
                className="workout-completion-brand-image"
                src={brandImage}
                alt=""
                aria-hidden="true"
              />
            )}
            <h1 className={"text-[40px]"}>Тренировка завершена!</h1>
          </section>

          <div className="workout-completion-summary-stage">
            <section
              ref={summaryRef}
              className="workout-completion-summary"
              aria-label="Краткий итог"
            >
              <span className="workout-completion-summary-label">
                <CompletionSvgIcon
                  className="workout-completion-summary-label-icon"
                  src={summaryIcon}
                />
                Summary
              </span>
              <p className="workout-completion-summary-text">
                <span className="workout-completion-summary-line">
                  Вы завершили
                  <CompletionSummaryPill
                    icon={<TrendingUp size={16} strokeWidth={2.35} />}
                  >
                    {progress}%
                  </CompletionSummaryPill>
                  тренировки,
                </span>
                <span className="workout-completion-summary-line">
                  потратив
                  <CompletionSummaryPill
                    icon={
                      <CompletionSvgIcon
                        className="workout-completion-summary-pill-icon"
                        src={flameIcon}
                      />
                    }
                  >
                    {calories}
                  </CompletionSummaryPill>
                  ккал за
                  <CompletionSummaryPill
                    icon={
                      <CompletionSvgIcon
                        className="workout-completion-summary-pill-icon"
                        src={timeIcon}
                      />
                    }
                  >
                    {totalMinutes}
                  </CompletionSummaryPill>
                </span>
                <span className="workout-completion-summary-line">
                  мин и
                  <CompletionSummaryPill
                    icon={
                      <CompletionSvgIcon
                        className="workout-completion-summary-pill-icon"
                        src={timeIcon}
                      />
                    }
                  >
                    {seconds}
                  </CompletionSummaryPill>
                  сек, выполнив
                </span>
                <span className="workout-completion-summary-line">
                  <CompletionSummaryPill
                    icon={<Dumbbell size={16} strokeWidth={2.35} />}
                  >
                    {completedExercises}
                  </CompletionSummaryPill>
                  упражнений.
                </span>
              </p>
            </section>
          </div>

          <section
            className="workout-completion-metrics"
            aria-label="Итоги тренировки"
          >
            <CompletionMetricCard
              accent="lime"
              icon={
                <CompletionSvgIcon
                  className="workout-completion-metric-svg-icon"
                  src={timeIcon}
                />
              }
              label="Общее время"
              value={String(totalMinutes)}
              suffix="мин"
              description="Длительность"
            />
            <CompletionMetricCard
              accent="red"
              icon={
                <CompletionSvgIcon
                  className="workout-completion-metric-svg-icon"
                  src={flameIcon}
                />
              }
              label="Калории"
              value={String(calories)}
              suffix="ккал"
              description="Потрачено"
            />
            <CompletionMetricCard
              accent="violet"
              icon={<Dumbbell size={21} strokeWidth={2.25} />}
              label="Упражнения"
              value={String(completedExercises)}
              suffix={`/ ${exercisesAmount}`}
              description="Выполнено"
            />
            <CompletionMetricCard
              accent="blue"
              icon={<TrendingUp size={20} strokeWidth={2.25} />}
              label="Прогресс"
              value={String(progress)}
              suffix="%"
              progressValue={progress}
              description="Тренировка завершена"
            />
          </section>

          <section className="workout-completion-actions" aria-label="Действия">
            {onDiscussWorkout && (
              <button
                type="button"
                className="workout-completion-chat"
                onClick={onDiscussWorkout}
              >
                <span className="workout-completion-home-icon" aria-hidden="true">
                  <Dumbbell size={17} strokeWidth={2.35} />
                </span>
                <strong>Обсудить тренировку</strong>
                <ArrowRight
                  className="workout-completion-action-arrow"
                  size={22}
                  strokeWidth={2.4}
                  aria-hidden="true"
                />
              </button>
            )}
            <button
              type="button"
              className="workout-completion-home"
              onClick={onHome}
            >
              <span className="workout-completion-home-icon" aria-hidden="true">
                <Home size={17} strokeWidth={2.35} />
              </span>
              <strong>На главную</strong>
              <ArrowRight
                className="workout-completion-action-arrow"
                size={22}
                strokeWidth={2.4}
                aria-hidden="true"
              />
            </button>
          </section>
        </main>
      </Content>
    </DefaultPage>
  );
};
